<template>
  <div class="profile-page">
    <div class="page-header">
      <h2 class="page-title">个人中心 / 账户设置</h2>
      <p class="page-desc">管理个人信息、账号与第三方服务配置</p>
    </div>

    <div class="profile-container">
      <!-- 三栏布局 -->
      <div class="profile-row">
        
        <!-- 基本信息（独立表单） -->
        <el-card class="profile-card" shadow="hover">
          <template #header>
            <div class="card-header-title">
              <el-icon><User /></el-icon>
              <span>基本信息</span>
            </div>
          </template>

          <el-form
            :model="form"
            :rules="formRules"
            ref="formRef"
            label-width="110px"
            class="profile-form"
          >
            <el-form-item label="登录账号" prop="account">
              <el-input v-model="form.account" disabled />
              <div class="form-tip">账号固定，不可修改</div>
            </el-form-item>

            <el-form-item label="商户昵称" prop="nickName">
              <el-input v-model="form.nickName" placeholder="请输入商户昵称" />
            </el-form-item>

            <el-form-item label="联系手机" prop="mobile">
              <el-input v-model="form.mobile" placeholder="请输入手机号" />
            </el-form-item>

            <el-form-item label="当前点数">
              <el-tag type="success" size="default">{{ form.dianshu }} 点</el-tag>
            </el-form-item>

            <el-form-item label="账号状态">
              <el-tag :type="form.is_disable === 0 ? 'success' : 'danger'">
                {{ form.is_disable === 0 ? '正常启用' : '已禁用' }}
              </el-tag>
            </el-form-item>

            <el-form-item label="创建时间">
              <span>{{ formatCreateTime(form.create_time) }}</span>
            </el-form-item>

            <el-form-item class="save-btn-item">
              <el-button type="primary" @click="saveBaseInfo" :loading="submitting.base" size="default" class="save-btn">
                <el-icon><Check /></el-icon>保存基本信息
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- 阿里云存储设置 -->
        <el-card class="profile-card" shadow="hover">
          <template #header>
            <div class="card-header-title">
              <el-icon><FolderOpened /></el-icon>
              <span>阿里云 OSS 存储配置</span>
            </div>
          </template>

          <el-form
            :model="ossForm"
            ref="ossFormRef"
            label-width="110px"
            class="profile-form"
          >
            <el-form-item label="阿里云ID">
              <el-input v-model="ossForm.aliyun_keyid" placeholder="请输入阿里云 AccessKey ID" />
            </el-form-item>

            <el-form-item label="阿里密钥">
              <el-input
                v-model="ossForm.aliyun_secret"
                placeholder="请输入阿里云 AccessKey Secret"
                show-password
              />
            </el-form-item>

            <el-form-item label="存储地域">
              <el-input v-model="ossForm.endpoint" placeholder="例：oss-cn-shanghai.aliyuncs.com" />
              <div class="form-tip">
                华东1：oss-cn-shanghai.aliyuncs.com<br>
                华北2：oss-cn-beijing.aliyuncs.com
              </div>
            </el-form-item>

            <el-form-item label="存储桶">
              <el-input v-model="ossForm.aliyun_bucket" placeholder="请输入Bucket名称" />
            </el-form-item>

            <el-form-item class="save-btn-item">
              <el-button type="primary" @click="saveOssInfo" :loading="submitting.oss" size="default" class="save-btn">
                <el-icon><Check /></el-icon>保存OSS配置
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>

        <!-- 腾讯地图设置 -->
        <el-card class="profile-card" shadow="hover">
          <template #header>
            <div class="card-header-title">
              <el-icon><MapLocation /></el-icon>
              <span>腾讯地图配置</span>
            </div>
          </template>

          <el-form
            :model="mapForm"
            ref="mapFormRef"
            label-width="110px"
            class="profile-form"
          >
            <el-form-item label="地图KEY">
              <el-input v-model="mapForm.tencent_map_key" placeholder="请输入腾讯地图 KEY" />
              <div class="form-tip">在腾讯位置服务控制台获取</div>
            </el-form-item>

            <el-form-item label="地图SK">
              <el-input
                v-model="mapForm.tencent_map_sk"
                placeholder="请输入腾讯地图 SK（可选）"
                show-password
              />
              <div class="form-tip">用于签名校验，可不填</div>
            </el-form-item>

            <el-form-item class="save-btn-item">
              <el-button type="primary" @click="saveMapInfo" :loading="submitting.map" size="default" class="save-btn">
                <el-icon><Check /></el-icon>保存地图配置
              </el-button>
            </el-form-item>
          </el-form>
        </el-card>

      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElLoading } from 'element-plus'
import Cookies from 'js-cookie'
import axios from 'axios'
import config from '@/config'
import { User, FolderOpened, Check, MapLocation } from '@element-plus/icons-vue'

export default {
  name: 'ProfilePage',
  components: { User, FolderOpened, Check, MapLocation },
  setup() {
    const userInfo = JSON.parse(Cookies.get('user_info') || '{}')
    const storeId = ref(userInfo.id || 0)
    const agentId = ref(userInfo.agent_id || 0)

    // 基本信息
    const formRef = ref(null)
    const form = reactive({
      id: storeId.value,
      account: userInfo.account || '',
      nickName: userInfo.nickName || '',
      mobile: userInfo.mobile || '',
      dianshu: userInfo.dianshu || '0.00',
      is_disable: userInfo.is_disable || 0,
      create_time: userInfo.create_time || 0,
    })

    // OSS 信息
    const ossFormRef = ref(null)
    const ossForm = reactive({
      aliyun_keyid: userInfo.aliyun_keyid || '',
      aliyun_secret: userInfo.aliyun_secret || '',
      endpoint: userInfo.endpoint || '',
      aliyun_bucket: userInfo.aliyun_bucket || ''
    })

    // 地图信息
    const mapFormRef = ref(null)
    const mapForm = reactive({
      tencent_map_key: userInfo.tencent_map_key || '',
      tencent_map_sk: userInfo.tencent_map_sk || ''
    })

    // 加载状态
    const submitting = reactive({
      base: false,
      oss: false,
      map: false
    })

    // 基本信息验证
    const formRules = {
      nickName: [{ required: true, message: '请输入商户昵称', trigger: 'blur' }],
      mobile: [
        { required: true, message: '请输入手机号', trigger: 'blur' },
        { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
      ]
    }

    // 加载用户信息
    const fetchProfile = async () => {
      const loading = ElLoading.service({ text: '加载中...' })
      try {
        const res = await axios.get(`${config.BASE_URL}/shop/api/member_api.php`, {
          params: {
            action: 'get_profile',
            store_id: storeId.value,
            agent_id: agentId.value
          }
        })
        if (res.data.code === 200) {
          const data = res.data.data
          Object.assign(form, data)
          Object.assign(ossForm, {
            aliyun_keyid: data.aliyun_keyid || '',
            aliyun_secret: data.aliyun_secret || '',
            endpoint: data.endpoint || '',
            aliyun_bucket: data.aliyun_bucket || ''
          })
          Object.assign(mapForm, {
            tencent_map_key: data.tencent_map_key || '',
            tencent_map_sk: data.tencent_map_sk || ''
          })
          Cookies.set('user_info', JSON.stringify(data), { expires: 7 })
        } else {
          ElMessage.error(res.data.message || '获取失败')
        }
      } catch (err) {
        console.error(err)
        ElMessage.error('获取个人信息失败')
      } finally {
        loading.close()
      }
    }

    // 构建完整提交数据
    const buildPayload = () => {
      return {
        action: 'update_profile',
        store_id: storeId.value,
        agent_id: agentId.value,
        nickName: form.nickName,
        mobile: form.mobile,
        aliyun_keyid: ossForm.aliyun_keyid,
        aliyun_secret: ossForm.aliyun_secret,
        endpoint: ossForm.endpoint,
        aliyun_bucket: ossForm.aliyun_bucket,
        tencent_map_key: mapForm.tencent_map_key,
        tencent_map_sk: mapForm.tencent_map_sk
      }
    }

    // 保存后更新Cookie
    const updateCookie = (payload) => {
      const currentUserInfo = JSON.parse(Cookies.get('user_info') || '{}')
      const updatedUserInfo = { ...currentUserInfo, ...payload }
      Cookies.set('user_info', JSON.stringify(updatedUserInfo), { expires: 7 })
    }

    // 保存基本信息
    const saveBaseInfo = async () => {
      const valid = await new Promise(resolve => {
        formRef.value?.validate(valid => resolve(valid))
      })
      if (!valid) return

      submitting.base = true
      const loading = ElLoading.service({ text: '保存中...' })

      try {
        const payload = buildPayload()
        const res = await axios.post(`${config.BASE_URL}/shop/api/member_api.php`, payload)

        if (res.data.code === 200) {
          ElMessage.success('基本信息保存成功！')
          updateCookie(payload)
          await fetchProfile()
        } else {
          ElMessage.error(res.data.message || '保存失败')
        }
      } catch (err) {
        ElMessage.error('保存失败')
      } finally {
        submitting.base = false
        loading.close()
      }
    }

    // 保存 OSS 配置
    const saveOssInfo = async () => {
      submitting.oss = true
      const loading = ElLoading.service({ text: '保存OSS配置中...' })

      try {
        const payload = buildPayload()
        const res = await axios.post(`${config.BASE_URL}/shop/api/member_api.php`, payload)

        if (res.data.code === 200) {
          ElMessage.success('OSS配置保存成功！')
          updateCookie(payload)
          await fetchProfile()
        } else {
          ElMessage.error(res.data.message || '保存失败')
        }
      } catch (err) {
        ElMessage.error('保存失败')
      } finally {
        submitting.oss = false
        loading.close()
      }
    }

    // 保存地图配置
    const saveMapInfo = async () => {
      submitting.map = true
      const loading = ElLoading.service({ text: '保存地图配置中...' })

      try {
        const payload = buildPayload()
        const res = await axios.post(`${config.BASE_URL}/shop/api/member_api.php`, payload)

        if (res.data.code === 200) {
          ElMessage.success('地图配置保存成功！')
          updateCookie(payload)
          await fetchProfile()
        } else {
          ElMessage.error(res.data.message || '保存失败')
        }
      } catch (err) {
        ElMessage.error('保存失败')
      } finally {
        submitting.map = false
        loading.close()
      }
    }

    // 时间格式化
    const formatCreateTime = (timestamp) => {
      if (!timestamp) return '-'
      const date = new Date(timestamp * 1000)
      return date.toLocaleString()
    }

    onMounted(() => {
      fetchProfile()
    })

    return {
      form,
      ossForm,
      mapForm,
      formRules,
      formRef,
      ossFormRef,
      mapFormRef,
      submitting,
      saveBaseInfo,
      saveOssInfo,
      saveMapInfo,
      formatCreateTime
    }
  }
}
</script>

<style scoped lang="scss">
.profile-page {
  padding: 0;
  width: 100%;
}

.page-header {
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a2e;
    margin-bottom: 4px;
  }

  .page-desc {
    font-size: 14px;
    color: #8c8c8c;
  }
}

.profile-container {
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
}

.profile-row {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.profile-card {
  flex: 1;
  min-width: 450px;
  border-radius: 12px;
  overflow: hidden;
}

.card-header-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 500;
}

.profile-form {
  margin-top: 12px;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  line-height: 1.4;
}

.save-btn-item {
  margin-top: 16px;
  display: flex;
  align-items: center;
}

.save-btn {
  padding: 8px 24px;
  font-size: 14px;
  font-weight: 500;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  border: none !important;
  transition: all 0.3s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled,
  &.is-loading {
    opacity: 0.8;
    transform: none;
  }
}

@media (max-width: 1440px) {
  .profile-card {
    min-width: 400px;
  }
}

@media (max-width: 1024px) {
  .profile-row {
    flex-direction: column;
  }
  .profile-card {
    min-width: 100%;
    width: 100%;
  }
}
</style>